import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Users, Repeat, AlertTriangle, Crown,
  ShoppingBasket, Sparkles, Download, Send,
  Star, PieChart, Zap, User, ArrowRight,
  Cake, UsersRound,
  GlassWater, Coffee, Wine, IceCreamCone, Sandwich, Beef, Fish,
  Soup, Salad, EggFried, Cookie, UtensilsCrossed,
  LucideIcon,
} from "lucide-react";

// Acepta tanto íconos de lucide como nuestros propios componentes SVG inline (Mars/Venus)
type IconCmp = LucideIcon | ((props: { size?: number; strokeWidth?: number; className?: string; "aria-hidden"?: boolean }) => JSX.Element);

// Símbolos de género (lucide < 0.481 no exporta Mars/Venus)
function Mars({ size = 16, className = "", "aria-hidden": ah }: { size?: number; className?: string; "aria-hidden"?: boolean }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden={ah}>
      <circle cx="10" cy="14" r="6" />
      <path d="M19 5l-5.4 5.4" /><path d="M19 5h-5" /><path d="M19 5v5" />
    </svg>
  );
}
function Venus({ size = 16, className = "", "aria-hidden": ah }: { size?: number; className?: string; "aria-hidden"?: boolean }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden={ah}>
      <circle cx="12" cy="9" r="6" />
      <path d="M12 15v7" /><path d="M9 19h6" />
    </svg>
  );
}

function iconoFamilia(familia: string): IconCmp {
  const f = familia.toLowerCase();
  if (/bebid|refresc|gase|jugo|agua|t[eé]/i.test(f)) return GlassWater;
  if (/caf[eé]|exp?reso/i.test(f)) return Coffee;
  if (/cerv|vino|licor|trag|cocktail|c[oó]ctel|whisk|ron|vodka|gin|alcoh/i.test(f)) return Wine;
  if (/post|dulc|torta|helad|cake/i.test(f)) return IceCreamCone;
  if (/pan|empan|hamb|hotdog|sandw|pizza|past|fideo|risott|spaghetti/i.test(f)) return Sandwich;
  if (/pollo|carn|res|cerd|costill|filet|chicharr|chorizo|parrill/i.test(f)) return Beef;
  if (/pesc|mar|cev|ostra|camar|atun|salmon|merluza/i.test(f)) return Fish;
  if (/sopa|caldo|crema/i.test(f)) return Soup;
  if (/ensal|verdura|vegetal/i.test(f)) return Salad;
  if (/desayun|huev|tortill|arepa/i.test(f)) return EggFried;
  if (/snack|aper|picad|entrada|tequ|ñoki/i.test(f)) return Cookie;
  return UtensilsCrossed;
}
function FamiliaIcon({ familia, size = 16, className = "" }: { familia: string; size?: number; className?: string }) {
  const Icon = iconoFamilia(familia);
  return <Icon size={size} strokeWidth={1.8} className={className} aria-hidden />;
}
import { cliente360Api } from "../../lib/api";
import { CampanaMasivaModal } from "./CampanaMasivaModal";

function fmtNum(n: number | string | null | undefined) {
  const v = Number(n ?? 0);
  return v.toLocaleString("es-VE");
}
function fmtUsd(n: number | string | null | undefined) {
  const v = Number(n ?? 0);
  return "$" + v.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const RANGO_COLOR: Record<string, string> = {
  "1 (individual)": "#94A3B8",
  "2": "#60A5FA",
  "3-4": "#34D399",
  "5-8": "#F59E0B",
  "9+": "#EF4444",
};

export function KpisAvanzadosPanel({ sucursalId }: { sucursalId?: number | null } = {}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["cliente360", "kpis-avanzados", sucursalId],
    queryFn: () => cliente360Api.kpisAvanzados(sucursalId ?? undefined),
  });
  const [campana, setCampana] = useState<null | { segmento: "vip" | "enRiesgo" }>(null);
  const [descargando, setDescargando] = useState<null | "vip" | "enRiesgo">(null);

  async function descargarCsv(tipo: "vip" | "enRiesgo") {
    setDescargando(tipo);
    try { await cliente360Api.exportarCsv(tipo); }
    catch (e: any) { alert(e.message || "Error al descargar"); }
    finally { setDescargando(null); }
  }

  if (isLoading) {
    return <div className="text-center py-10 text-ink-500">Calculando KPIs avanzados…</div>;
  }
  if (isError) {
    return <div className="text-center py-10 text-red-600 text-sm">No se pudieron cargar los KPIs avanzados. Probá recargar la página.</div>;
  }
  if (!data) return null;

  const maxTicketComensal = Math.max(
    ...data.ticketPorTamano.map((t) => Number(t.ticketPorComensal)),
    0.0001
  );
  const maxFamiliaVentas = Math.max(
    ...data.topFamilias.map((f) => Number(f.ventasUsd)),
    0.0001
  );

  return (
    <div className="space-y-5">
      {/* TICKET POR COMENSAL */}
      <section className="card p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-1">
          <Users className="size-5 text-indigo-600" />
          <h3 className="font-display font-bold text-ink-800 text-lg">
            Ticket promedio por tamaño de grupo
          </h3>
        </div>
        <p className="text-xs text-ink-500 mb-4">
          Compara el monto promedio por comensal y por visita según cuánta gente vino.
        </p>

        <div className="space-y-2.5">
          {data.ticketPorTamano.map((t) => {
            const ancho = (Number(t.ticketPorComensal) / maxTicketComensal) * 100;
            const color = RANGO_COLOR[t.rango] ?? "#6B7280";
            return (
              <div key={t.rango} className="grid grid-cols-12 items-center gap-3 text-sm">
                <div className="col-span-3 sm:col-span-2 font-medium text-ink-700">
                  {t.rango}
                </div>
                <div className="col-span-5 sm:col-span-6">
                  <div className="h-7 rounded-md bg-ink-100 relative overflow-hidden">
                    <div className="h-full transition-all"
                      style={{ width: `${ancho}%`, backgroundColor: color }} />
                    {/* Etiqueta:
                        - Si la barra es ancha (>=55%) → texto oscuro DENTRO de la barra, a la derecha
                        - Si la barra es corta (<55%)  → texto oscuro FUERA, pegado al final del color */}
                    {ancho >= 55 ? (
                      <span className="absolute inset-0 flex items-center justify-end pr-2 text-xs font-semibold text-ink-700">
                        {fmtUsd(t.ticketPorComensal)} / comensal
                      </span>
                    ) : (
                      <span
                        className="absolute top-0 h-full flex items-center pl-2 text-xs font-semibold text-ink-700 whitespace-nowrap"
                        style={{ left: `${ancho}%` }}
                      >
                        {fmtUsd(t.ticketPorComensal)} <span className="text-ink-500 ml-1">/ comensal</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-span-2 text-right text-ink-600">
                  {fmtNum(t.visitas)} <span className="text-ink-400 text-xs">vis.</span>
                </div>
                <div className="col-span-2 text-right text-ink-700 font-medium">
                  {fmtUsd(t.ticketPromedio)}
                  <div className="text-[10px] text-ink-400">ticket total</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* RETENCIÓN */}
      {data.retencion && (
        <section className="card p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <Repeat className="size-5 text-emerald-600" />
            <h3 className="font-display font-bold text-ink-800 text-lg">
              Tasa de retorno
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KpiBox label="Clientes únicos" value={fmtNum(data.retencion.clientesUnicos)} />
            <KpiBox label="Solo 1 visita" value={fmtNum(data.retencion.soloUnaVez)} colorClass="text-rose-600" />
            <KpiBox label="2 – 4 visitas" value={fmtNum(data.retencion.recurrentes)} colorClass="text-amber-600" />
            <KpiBox label="5+ visitas (fieles)" value={fmtNum(data.retencion.fidelizados)} colorClass="text-emerald-600" />
            <KpiBox label="Tasa de retorno"
              value={`${data.retencion.tasaRetornoPct ?? 0}%`}
              colorClass="text-indigo-600" big />
          </div>
          <p className="text-xs text-ink-500 mt-3">
            Visitas promedio por cliente: <b>{data.retencion.visitasPromedio}</b>.
            La <b>tasa de retorno</b> es el % de clientes con 2 o más visitas.
          </p>
        </section>
      )}

      {/* COMPARATIVA POR SEXO */}
      <SexoSection data={data.sexoStats} />

      {/* DISTRIBUCIÓN POR EDAD */}
      <EdadSection data={data.distribucionEdad} />

      {/* TOP FAMILIAS */}
      <section className="card p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShoppingBasket className="size-5 text-orange-600" />
          <h3 className="font-display font-bold text-ink-800 text-lg">
            Familias / grupos más vendidos
          </h3>
        </div>
        <div className="space-y-2">
          {data.topFamilias.slice(0, 10).map((f) => {
            const ancho = (Number(f.ventasUsd) / maxFamiliaVentas) * 100;
            return (
              <div key={f.familia} className="grid grid-cols-12 items-center gap-3 text-sm">
                <div className="col-span-4 truncate font-medium text-ink-700" title={f.familia}>
                  {f.familia}
                </div>
                <div className="col-span-5">
                  <div className="h-6 rounded-md bg-orange-100 relative overflow-hidden">
                    <div className="h-full bg-orange-500" style={{ width: `${ancho}%` }} />
                  </div>
                </div>
                <div className="col-span-2 text-right text-ink-700 font-medium">{fmtUsd(f.ventasUsd)}</div>
                <div className="col-span-1 text-right text-ink-500 text-xs">{fmtNum(f.unidadesVendidas)}u</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* HÁBITOS DEL LOCAL — 3 vistas (últimos 12 meses) */}
      <HabitosGeneralesSection data={data.habitosGenerales} />


      {/* SEGMENTO VIP */}
      <section className="card p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
          <div className="flex items-center gap-2">
            <Crown className="size-5 text-amber-500" />
            <h3 className="font-display font-bold text-ink-800 text-lg">
              Clientes VIP — alto ticket + recurrencia
            </h3>
          </div>
          <div className="flex gap-2">
            <button onClick={() => descargarCsv("vip")} disabled={descargando === "vip"}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 transition disabled:opacity-50">
              <Download className="size-3.5" />
              {descargando === "vip" ? "Descargando…" : "Exportar CSV"}
            </button>
            <button onClick={() => setCampana({ segmento: "vip" })}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition">
              <Send className="size-3.5" /> Enviar campaña
            </button>
          </div>
        </div>
        <p className="text-xs text-ink-500 mb-3">
          3+ visitas con ticket promedio ≥ $20. Candidatos ideales para campañas de fidelización.
        </p>
        <ClientesTable rows={data.segmentoVip.map(c => ({
          id: c.codCliente, nombre: c.nombre, email: c.email, telefono: c.telefono,
          sexo: c.sexo, visitas: c.visitas,
          ticketPromedio: c.ticketPromedio, gastoTotal: c.gastoTotal,
        }))} />
      </section>

      {/* EN RIESGO */}
      <section className="card p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
          <div className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-rose-500" />
            <h3 className="font-display font-bold text-ink-800 text-lg">
              Clientes en riesgo — valiosos pero ausentes
            </h3>
          </div>
          <div className="flex gap-2">
            <button onClick={() => descargarCsv("enRiesgo")} disabled={descargando === "enRiesgo"}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 transition disabled:opacity-50">
              <Download className="size-3.5" />
              {descargando === "enRiesgo" ? "Descargando…" : "Exportar CSV"}
            </button>
            <button onClick={() => setCampana({ segmento: "enRiesgo" })}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-rose-500 text-white hover:bg-rose-600 transition">
              <Send className="size-3.5" /> Recuperar
            </button>
          </div>
        </div>
        <p className="text-xs text-ink-500 mb-3">
          2+ visitas, ticket ≥ $15 y entre 60 y 365 días sin volver. Mandales un mensaje antes de perderlos.
        </p>
        <ClientesTable
          rows={data.enRiesgo.map(c => ({
            id: c.codCliente, nombre: c.nombre, email: c.email, telefono: c.telefono,
            sexo: c.sexo, visitas: c.visitas,
            ticketPromedio: c.ticketPromedio, gastoTotal: c.gastoTotal,
            extra: `${c.diasSinVolver} días`,
          }))}
          extraLabel="Sin volver"
        />
      </section>

      {/* Modal campaña masiva */}
      <CampanaMasivaModal
        open={!!campana}
        segmento={campana?.segmento ?? "vip"}
        onClose={() => setCampana(null)}
      />
    </div>
  );
}

function KpiBox({ label, value, colorClass = "text-ink-800", big = false }: {
  label: string; value: string; colorClass?: string; big?: boolean;
}) {
  return (
    <div className="rounded-lg border border-ink-200 bg-paper-50 p-3">
      <div className={`font-bold leading-none ${big ? "text-3xl" : "text-2xl"} ${colorClass}`}>{value}</div>
      <div className="text-[11px] text-ink-500 mt-1">{label}</div>
    </div>
  );
}

interface RowCliente {
  id: number;
  nombre: string;
  email?: string;
  telefono?: string;
  sexo?: string;
  visitas: number;
  ticketPromedio: string;
  gastoTotal: string;
  extra?: string;
}
function ClientesTable({ rows, extraLabel }: { rows: RowCliente[]; extraLabel?: string }) {
  if (rows.length === 0) {
    return <p className="text-sm text-ink-400 py-2">No hay clientes que cumplan los criterios.</p>;
  }
  return (
    <div className="overflow-x-auto -mx-2 sm:mx-0">
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase text-ink-500 border-b border-ink-200">
          <tr>
            <th className="text-left px-2 py-2">Cliente</th>
            <th className="text-right px-2 py-2">Visitas</th>
            <th className="text-right px-2 py-2">Ticket prom.</th>
            <th className="text-right px-2 py-2">Gasto total</th>
            {extraLabel && <th className="text-right px-2 py-2">{extraLabel}</th>}
            <th className="text-right px-2 py-2">Acción</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-ink-100 hover:bg-paper-50">
              <td className="px-2 py-2">
                <div className="font-medium text-ink-800 flex items-center gap-1.5">
                  {r.nombre}
                  {r.sexo === "F" && <Venus size={12} className="text-pink-500 inline" aria-hidden />}
                  {r.sexo === "M" && <Mars size={12} className="text-blue-500 inline" aria-hidden />}
                </div>
                <div className="text-xs text-ink-500">
                  {[r.email, r.telefono].filter(Boolean).join(" · ")}
                </div>
              </td>
              <td className="text-right text-ink-700">{r.visitas}</td>
              <td className="text-right text-ink-700">{fmtUsd(r.ticketPromedio)}</td>
              <td className="text-right text-ink-800 font-semibold">{fmtUsd(r.gastoTotal)}</td>
              {extraLabel && <td className="text-right text-rose-600 font-medium">{r.extra}</td>}
              <td className="text-right">
                <Link to={`/cliente360/${r.id}`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-brand-100 text-brand-700 hover:bg-brand-200 transition">
                  Ver
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// DISTRIBUCIÓN POR EDAD (estimada por CI venezolana)
// ────────────────────────────────────────────────────────────
interface EdadDist {
  conteos: {
    conFechaReal: number; conCedulaEstimada: number;
    confianzaBaja: number; sinDeterminar: number;
    totalAnalizables: number;
  };
  rangos: Array<{
    rango: "<20" | "20-30" | "30-40" | "40-60" | "60+";
    clientes: number; visitas: number; gastoUsd: number;
    masculinos: number; femeninos: number; ticketPromedio: number;
  }>;
}

const RANGO_COLOR_EDAD: Record<string, string> = {
  "<20":   "#A78BFA",
  "20-30": "#60A5FA",
  "30-40": "#34D399",
  "40-60": "#F59E0B",
  "60+":   "#F87171",
};
const RANGO_LABEL_EDAD: Record<string, string> = {
  "<20":   "Menores de 20",
  "20-30": "20 a 30 años",
  "30-40": "30 a 40 años",
  "40-60": "40 a 60 años",
  "60+":   "60+ años",
};
// Etiqueta compacta en lugar de emoji por rango — el badge de color comunica el segmento
const RANGO_BADGE_COLOR: Record<string, string> = {
  "<20": "bg-violet-100 text-violet-700 border-violet-200",
  "20-30": "bg-sky-100 text-sky-700 border-sky-200",
  "30-40": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "40-60": "bg-amber-100 text-amber-700 border-amber-200",
  "60+":   "bg-rose-100 text-rose-700 border-rose-200",
};

function EdadSection({ data }: { data: EdadDist }) {
  const total = data.conteos.totalAnalizables;

  if (total === 0) {
    return (
      <section className="card p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-2">
          <Cake size={20} className="text-amber-500" aria-hidden />
          <h3 className="font-display font-bold text-ink-800 text-lg">Distribución por edad</h3>
        </div>
        <p className="text-sm text-ink-400">
          Sin clientes con CI ni fecha de nacimiento todavía. Cargá el documento de identidad
          en Cliente 360° para estimar edad automáticamente.
        </p>
      </section>
    );
  }

  const maxClientes = Math.max(...data.rangos.map((r) => r.clientes), 1);
  const totalGasto = data.rangos.reduce((s, r) => s + r.gastoUsd, 0);
  const totalVisitas = data.rangos.reduce((s, r) => s + r.visitas, 0);

  return (
    <section className="card p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-1">
        <Cake size={20} className="text-amber-500" aria-hidden />
        <h3 className="font-display font-bold text-ink-800 text-lg">
          Distribución por edad
        </h3>
      </div>
      <p className="text-xs text-ink-500 mb-3">
        {fmtNum(data.conteos.conFechaReal)} con fecha real ·{" "}
        {fmtNum(data.conteos.conCedulaEstimada)} estimados por cédula venezolana
        {data.conteos.confianzaBaja > 0 && (
          <span className="text-amber-600">
            {" "}· {fmtNum(data.conteos.confianzaBaja)} con confianza baja (omitidos)
          </span>
        )}
      </p>

      {/* Barra apilada total */}
      <div className="h-3 rounded-full overflow-hidden bg-ink-100 flex mb-4">
        {data.rangos.map((r) => {
          if (r.clientes === 0) return null;
          const pct = (r.clientes / total) * 100;
          return (
            <div key={r.rango} title={`${RANGO_LABEL_EDAD[r.rango]}: ${r.clientes} (${pct.toFixed(0)}%)`}
              style={{ width: `${pct}%`, backgroundColor: RANGO_COLOR_EDAD[r.rango] }} />
          );
        })}
      </div>

      {/* Tabla por rango */}
      <div className="space-y-2">
        {data.rangos.map((r) => {
          const pct = (r.clientes / total) * 100;
          const ancho = (r.clientes / maxClientes) * 100;
          return (
            <div key={r.rango} className="grid grid-cols-12 items-center gap-2 text-sm">
              <div className="col-span-12 sm:col-span-3 flex items-center gap-2">
                <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-md text-[10px] font-bold border ${RANGO_BADGE_COLOR[r.rango]}`}>
                  {r.rango}
                </span>
                <span className="font-medium text-ink-800">{RANGO_LABEL_EDAD[r.rango]}</span>
              </div>
              <div className="col-span-7 sm:col-span-4">
                <div className="h-6 rounded-md bg-ink-100 relative overflow-hidden">
                  <div className="h-full transition-all"
                    style={{ width: `${ancho}%`, backgroundColor: RANGO_COLOR_EDAD[r.rango] }} />
                  <span className="absolute inset-0 flex items-center px-2 text-xs font-semibold text-ink-700">
                    {fmtNum(r.clientes)} ({pct.toFixed(0)}%)
                  </span>
                </div>
              </div>
              <div className="col-span-2 sm:col-span-1 text-right text-xs">
                <span className="inline-flex items-center gap-1 text-blue-600"><Mars size={11} aria-hidden /> {r.masculinos}</span>
              </div>
              <div className="col-span-3 sm:col-span-1 text-right text-xs">
                <span className="inline-flex items-center gap-1 text-pink-600"><Venus size={11} aria-hidden /> {r.femeninos}</span>
              </div>
              <div className="col-span-6 sm:col-span-2 text-right text-ink-600 text-xs">
                {fmtNum(r.visitas)} <span className="text-ink-400">visitas</span>
              </div>
              <div className="col-span-6 sm:col-span-1 text-right font-bold text-ink-800">
                {fmtUsd(r.gastoUsd)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Totales */}
      <div className="mt-4 pt-3 border-t border-ink-200 grid grid-cols-3 gap-3 text-center">
        <div>
          <div className="text-[10px] text-ink-500 uppercase tracking-wide">Clientes analizados</div>
          <div className="font-bold text-ink-800 text-lg">{fmtNum(total)}</div>
        </div>
        <div>
          <div className="text-[10px] text-ink-500 uppercase tracking-wide">Visitas totales</div>
          <div className="font-bold text-ink-800 text-lg">{fmtNum(totalVisitas)}</div>
        </div>
        <div>
          <div className="text-[10px] text-ink-500 uppercase tracking-wide">Gasto total</div>
          <div className="font-bold text-ink-800 text-lg">{fmtUsd(totalGasto)}</div>
        </div>
      </div>

      <p className="text-[10px] text-ink-400 mt-3 italic">
        Estimación por cédula venezolana basada en 6 puntos de calibración.
        Para mayor precisión, cargá la fecha de nacimiento real en Cliente 360°.
      </p>
    </section>
  );
}

// ────────────────────────────────────────────────────────────
// HÁBITOS GENERALES (mismo formato que por-cliente, pero del local entero)
// ────────────────────────────────────────────────────────────
interface HabitosGenerales {
  conteos: { individuales: number; grupales: number; total: number };
  individual: ContextoGen;
  grupo: ContextoGen;
}
interface ContextoGen {
  imprescindibles: Array<{ codArticulo: number; nombre: string; familia: string;
    tickets: number; pctTickets: number; }>;
  composicion: Array<{ familia: string; gastoUsd: number; unidades: number;
    pctGasto: number; pctUnidades: number; }>;
  combos: Array<{ codX: number; nombreX: string; familiaX: string; vecesX: number;
    codY: number; nombreY: string; familiaY: string;
    vecesJuntos: number; confianzaPct: number; }>;
}

const PALETA_FAM = ["#0EA5E9", "#F97316", "#10B981", "#EC4899", "#8B5CF6",
                    "#F59E0B", "#06B6D4", "#EF4444", "#84CC16", "#6366F1"];
function colorFam(familia: string): string {
  let h = 0;
  for (let i = 0; i < familia.length; i++) h = (h * 31 + familia.charCodeAt(i)) % 1000;
  return PALETA_FAM[h % PALETA_FAM.length];
}

function HabitosGeneralesSection({ data }: { data: HabitosGenerales }) {
  const [tab, setTab] = useState<"individual" | "grupo">(
    data.conteos.grupales > data.conteos.individuales ? "grupo" : "individual"
  );
  const activo = tab === "individual" ? data.individual : data.grupo;
  const totalCtx = tab === "individual" ? data.conteos.individuales : data.conteos.grupales;

  return (
    <section className="card p-0 overflow-hidden">
      <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-3 border-b border-ink-200">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="size-5 text-purple-600" />
          <h3 className="font-display font-bold text-ink-800 text-lg">
            Hábitos del local (últimos 12 meses)
          </h3>
        </div>
        <p className="text-xs text-ink-500">
          {fmtNum(data.conteos.total)} tickets analizados — {fmtNum(data.conteos.individuales)} individuales · {fmtNum(data.conteos.grupales)} con grupo
        </p>
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-2 border-b border-ink-200">
        <button onClick={() => setTab("individual")}
          className={`px-4 py-3 flex items-center justify-center gap-2 text-sm font-medium border-b-2 transition ${
            tab === "individual"
              ? "border-purple-500 text-purple-700 bg-purple-50/40"
              : "border-transparent text-ink-500 hover:bg-ink-50"
          }`}>
          <User className="size-4" />
          Cuando vienen solos
          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
            tab === "individual" ? "bg-purple-100 text-purple-700" : "bg-ink-100 text-ink-500"
          }`}>{fmtNum(data.conteos.individuales)}</span>
        </button>
        <button onClick={() => setTab("grupo")}
          className={`px-4 py-3 flex items-center justify-center gap-2 text-sm font-medium border-b-2 transition ${
            tab === "grupo"
              ? "border-purple-500 text-purple-700 bg-purple-50/40"
              : "border-transparent text-ink-500 hover:bg-ink-50"
          }`}>
          <Users className="size-4" />
          Cuando vienen en grupo
          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
            tab === "grupo" ? "bg-purple-100 text-purple-700" : "bg-ink-100 text-ink-500"
          }`}>{fmtNum(data.conteos.grupales)}</span>
        </button>
      </div>

      {totalCtx === 0 ? (
        <div className="p-8 text-center text-ink-400 text-sm">
          Sin tickets en este contexto durante los últimos 12 meses
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-ink-100">
          {/* IMPRESCINDIBLES */}
          <BloqueGen
            icono={<Star className="size-4 text-amber-500" />}
            titulo="Lo que casi siempre piden"
            subtitulo={`Productos en al menos 5% de los ${fmtNum(totalCtx)} tickets`}
          >
            {activo.imprescindibles.length === 0 ? (
              <p className="text-xs text-ink-400 italic">Sin productos repetidos lo suficiente</p>
            ) : (
              <ul className="space-y-2">
                {activo.imprescindibles.map((p) => (
                  <li key={p.codArticulo}>
                    <div className="flex items-center justify-between gap-2 mb-1 text-sm">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <FamiliaIcon familia={p.familia} size={15} className="shrink-0 text-ink-500" />
                        <div className="min-w-0 flex-1">
                          <div className="text-ink-800 font-medium truncate" title={p.nombre}>
                            {p.nombre}
                          </div>
                          <div className="text-[10px] text-ink-400 truncate">{p.familia}</div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-bold text-ink-800">{p.pctTickets}%</div>
                        <div className="text-[10px] text-ink-400">{fmtNum(p.tickets)} tickets</div>
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full bg-amber-100 overflow-hidden">
                      <div className="h-full bg-amber-500" style={{ width: `${Math.min(p.pctTickets, 100)}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </BloqueGen>

          {/* COMPOSICIÓN */}
          <BloqueGen
            icono={<PieChart className="size-4 text-emerald-600" />}
            titulo="Cómo se reparte el gasto"
            subtitulo="Proporción de gasto por familia"
          >
            {activo.composicion.length === 0 ? (
              <p className="text-xs text-ink-400 italic">Sin datos suficientes</p>
            ) : (
              <>
                {/* Barra apilada */}
                <div className="h-3 rounded-full overflow-hidden bg-ink-100 flex mb-3">
                  {activo.composicion.map((i) => (
                    <div key={i.familia} title={`${i.familia}: ${i.pctGasto}%`}
                      style={{ width: `${i.pctGasto}%`, backgroundColor: colorFam(i.familia) }} />
                  ))}
                </div>
                <ul className="space-y-1.5">
                  {activo.composicion.map((i) => (
                    <li key={i.familia} className="flex items-center gap-2 text-sm">
                      <span className="inline-block w-3 h-3 rounded-sm shrink-0"
                        style={{ backgroundColor: colorFam(i.familia) }} />
                      <FamiliaIcon familia={i.familia} size={15} className="shrink-0 text-ink-500" />
                      <span className="text-ink-700 truncate flex-1" title={i.familia}>{i.familia}</span>
                      <span className="text-ink-500 text-xs">{fmtUsd(i.gastoUsd)}</span>
                      <span className="font-bold text-ink-800 w-10 text-right">{i.pctGasto}%</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </BloqueGen>

          {/* COMBOS */}
          <BloqueGen
            icono={<Zap className="size-4 text-indigo-600" />}
            titulo="Combos predecibles"
            subtitulo="Confianza ≥ 50% · soporte ≥ 20 tickets"
          >
            {activo.combos.length === 0 ? (
              <p className="text-xs text-ink-400 italic">Sin combos con suficiente fuerza</p>
            ) : (
              <ul className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {activo.combos.map((c) => (
                  <li key={`${c.codX}-${c.codY}`}
                    className="rounded-lg border border-indigo-100 bg-indigo-50/40 px-3 py-2 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-ink-800 inline-flex items-center gap-1 min-w-0 truncate">
                        <FamiliaIcon familia={c.familiaX} size={14} className="shrink-0 text-indigo-500" />
                        <span className="truncate">{c.nombreX}</span>
                      </span>
                      <ArrowRight className="size-3.5 text-indigo-500 shrink-0" />
                      <span className="font-medium text-ink-800 inline-flex items-center gap-1 min-w-0 truncate">
                        <FamiliaIcon familia={c.familiaY} size={14} className="shrink-0 text-indigo-500" />
                        <span className="truncate">{c.nombreY}</span>
                      </span>
                      <span className="ml-auto text-xs font-bold text-indigo-700 bg-white border border-indigo-200 rounded-full px-2 py-0.5 shrink-0">
                        {c.confianzaPct}%
                      </span>
                    </div>
                    <p className="text-[11px] text-ink-500 mt-1">
                      {fmtNum(c.vecesJuntos)} de {fmtNum(c.vecesX)} tickets con "{c.nombreX}"
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </BloqueGen>
        </div>
      )}
    </section>
  );
}

// ────────────────────────────────────────────────────────────
// COMPARATIVA POR SEXO
// ────────────────────────────────────────────────────────────
interface ResumenSexo {
  sexo: string; clientesUnicos: number; visitasTotal: number;
  visitasPromedio: number; ticketPromedioUsd: number; gastoTotalUsd: number;
  gastoPromedioPorCliente: number; recurrentes: number; tasaRetornoPct: number;
}

function SexoSection({
  data,
}: {
  data: {
    resumen: ResumenSexo[];
    composicionM: Array<{ familia: string; gastoUsd: number; unidades: number; pctGasto: number; }>;
    composicionF: Array<{ familia: string; gastoUsd: number; unidades: number; pctGasto: number; }>;
  };
}) {
  const m = data.resumen.find((r) => r.sexo === "M");
  const f = data.resumen.find((r) => r.sexo === "F");
  const x = data.resumen.find((r) => r.sexo === "X");

  if (!m && !f) {
    return (
      <section className="card p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-2">
          <UsersRound size={20} className="text-indigo-500" aria-hidden />
          <h3 className="font-display font-bold text-ink-800 text-lg">Comparativa por sexo</h3>
        </div>
        <p className="text-sm text-ink-400">
          Aún no tenés clientes con sexo registrado. Andá a Cliente 360° y sugerí el sexo por nombre.
        </p>
      </section>
    );
  }

  // Para barras comparativas
  const maxTicket = Math.max(m?.ticketPromedioUsd ?? 0, f?.ticketPromedioUsd ?? 0, 0.01);
  const maxGasto = Math.max(m?.gastoPromedioPorCliente ?? 0, f?.gastoPromedioPorCliente ?? 0, 0.01);
  const maxVisitas = Math.max(m?.visitasPromedio ?? 0, f?.visitasPromedio ?? 0, 0.01);
  const maxRetorno = Math.max(m?.tasaRetornoPct ?? 0, f?.tasaRetornoPct ?? 0, 1);

  // "Quién gana" en cada métrica
  const ganador = (a?: number, b?: number) => {
    if (!a || !b) return null;
    if (Math.abs(a - b) / Math.max(a, b) < 0.05) return "empate"; // <5% diferencia
    return a > b ? "M" : "F";
  };

  return (
    <section className="card p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-1">
        <UsersRound size={20} className="text-indigo-500" aria-hidden />
        <h3 className="font-display font-bold text-ink-800 text-lg">
          Comparativa por sexo
        </h3>
      </div>
      <p className="text-xs text-ink-500 mb-4">
        Quién gasta más, vuelve más y qué prefieren las mujeres vs los hombres.
        {x && x.clientesUnicos > 0 && (
          <span className="text-amber-600">
            {" "}({fmtNum(x.clientesUnicos)} clientes sin clasificar)
          </span>
        )}
      </p>

      {/* Resumen lado a lado */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <SexoBox sexo="M" data={m} accent="blue" />
        <SexoBox sexo="F" data={f} accent="pink" />
      </div>

      {/* Comparativas */}
      <div className="space-y-3 mb-5">
        <CompararBarra
          label="Visitas promedio por cliente"
          valM={m?.visitasPromedio ?? 0}
          valF={f?.visitasPromedio ?? 0}
          max={maxVisitas}
          ganador={ganador(m?.visitasPromedio, f?.visitasPromedio)}
        />
        <CompararBarra
          label="Ticket promedio"
          valM={m?.ticketPromedioUsd ?? 0}
          valF={f?.ticketPromedioUsd ?? 0}
          max={maxTicket}
          ganador={ganador(m?.ticketPromedioUsd, f?.ticketPromedioUsd)}
          formato="usd"
        />
        <CompararBarra
          label="Gasto promedio por cliente"
          valM={m?.gastoPromedioPorCliente ?? 0}
          valF={f?.gastoPromedioPorCliente ?? 0}
          max={maxGasto}
          ganador={ganador(m?.gastoPromedioPorCliente, f?.gastoPromedioPorCliente)}
          formato="usd"
        />
        <CompararBarra
          label="Tasa de retorno"
          valM={m?.tasaRetornoPct ?? 0}
          valF={f?.tasaRetornoPct ?? 0}
          max={maxRetorno}
          ganador={ganador(m?.tasaRetornoPct, f?.tasaRetornoPct)}
          formato="pct"
        />
      </div>

      {/* Top familias por sexo */}
      <div className="grid md:grid-cols-2 gap-4">
        <CompPanel
          titulo="Qué prefieren los hombres"
          Icon={Mars}
          color="blue"
          items={data.composicionM}
        />
        <CompPanel
          titulo="Qué prefieren las mujeres"
          Icon={Venus}
          color="pink"
          items={data.composicionF}
        />
      </div>
    </section>
  );
}

function SexoBox({ sexo, data, accent }: {
  sexo: "M" | "F";
  data?: ResumenSexo;
  accent: "blue" | "pink";
}) {
  const colorClass = accent === "blue"
    ? "border-blue-200 bg-blue-50/40"
    : "border-pink-200 bg-pink-50/40";
  const labelClass = accent === "blue" ? "text-blue-700" : "text-pink-700";
  const iconColor = accent === "blue" ? "text-blue-500" : "text-pink-500";
  return (
    <div className={`rounded-xl border-2 p-3 ${colorClass}`}>
      <div className={`flex items-center gap-2 ${labelClass}`}>
        {sexo === "M"
          ? <Mars size={22} className={iconColor} aria-hidden />
          : <Venus size={22} className={iconColor} aria-hidden />}
        <span className="font-bold text-sm">{sexo === "M" ? "Hombres" : "Mujeres"}</span>
      </div>
      {!data ? (
        <p className="text-xs text-ink-400 mt-2">Sin datos</p>
      ) : (
        <div className="mt-2 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-ink-500 text-xs">Clientes únicos</span>
            <span className="font-semibold">{fmtNum(data.clientesUnicos)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-500 text-xs">Visitas totales</span>
            <span className="font-semibold">{fmtNum(data.visitasTotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-500 text-xs">Gasto total</span>
            <span className="font-semibold">{fmtUsd(data.gastoTotalUsd)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-500 text-xs">Recurrentes</span>
            <span className="font-semibold">{fmtNum(data.recurrentes)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function CompararBarra({
  label, valM, valF, max, ganador, formato = "num",
}: {
  label: string; valM: number; valF: number; max: number;
  ganador: "M" | "F" | "empate" | null;
  formato?: "num" | "usd" | "pct";
}) {
  const fmt = (v: number) =>
    formato === "usd" ? fmtUsd(v) :
    formato === "pct" ? `${v}%` :
    fmtNum(v);
  const pctM = (valM / max) * 100;
  const pctF = (valF / max) * 100;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5 text-xs">
        <span className="font-medium text-ink-700">{label}</span>
        {ganador && ganador !== "empate" && (
          <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
            ganador === "M" ? "bg-blue-100 text-blue-700" : "bg-pink-100 text-pink-700"
          }`}>
            {ganador === "M"
              ? <><Mars size={10} aria-hidden /> ganan hombres</>
              : <><Venus size={10} aria-hidden /> ganan mujeres</>}
          </span>
        )}
        {ganador === "empate" && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-ink-100 text-ink-600">
            empate
          </span>
        )}
      </div>
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-xs">
          <Mars size={12} className="text-blue-500" aria-hidden />
          <div className="flex-1 h-3 rounded-full bg-blue-100 overflow-hidden">
            <div className="h-full bg-blue-500" style={{ width: `${pctM}%` }} />
          </div>
          <span className="w-20 text-right font-semibold text-ink-700">{fmt(valM)}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Venus size={12} className="text-pink-500" aria-hidden />
          <div className="flex-1 h-3 rounded-full bg-pink-100 overflow-hidden">
            <div className="h-full bg-pink-500" style={{ width: `${pctF}%` }} />
          </div>
          <span className="w-20 text-right font-semibold text-ink-700">{fmt(valF)}</span>
        </div>
      </div>
    </div>
  );
}

function CompPanel({
  titulo, Icon, color, items,
}: {
  titulo: string; Icon: IconCmp; color: "blue" | "pink";
  items: Array<{ familia: string; gastoUsd: number; unidades: number; pctGasto: number; }>;
}) {
  const accent = color === "blue" ? "text-blue-700" : "text-pink-700";
  const bg = color === "blue" ? "bg-blue-100" : "bg-pink-100";
  const fg = color === "blue" ? "bg-blue-500" : "bg-pink-500";
  return (
    <div className="rounded-lg border border-ink-200 p-3">
      <h4 className={`font-semibold ${accent} text-sm mb-2 flex items-center gap-1.5`}>
        <Icon size={16} aria-hidden /> {titulo}
      </h4>
      {items.length === 0 ? (
        <p className="text-xs text-ink-400 italic">Sin datos</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((i) => (
            <li key={i.familia} className="text-sm">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-ink-700 truncate flex-1 mr-2 inline-flex items-center gap-1.5" title={i.familia}>
                  <FamiliaIcon familia={i.familia} size={13} className="shrink-0" /> {i.familia}
                </span>
                <span className="font-bold text-ink-800 text-xs">{i.pctGasto}%</span>
              </div>
              <div className={`h-1.5 rounded-full ${bg} overflow-hidden`}>
                <div className={`h-full ${fg}`} style={{ width: `${i.pctGasto}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BloqueGen({ icono, titulo, subtitulo, children }: {
  icono: React.ReactNode; titulo: string; subtitulo: string; children: React.ReactNode;
}) {
  return (
    <div className="p-4 sm:p-5">
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-0.5">
          {icono}
          <h4 className="font-semibold text-ink-800 text-sm">{titulo}</h4>
        </div>
        <p className="text-[11px] text-ink-500">{subtitulo}</p>
      </div>
      {children}
    </div>
  );
}
